import { environment } from "@/environments/environment";
import {
    OptionDto,
    VehicleAccessoryAvailabilityDto,
    VehicleDamageDto,
    VehicleDamageRecordDto,
    VehicleDocumentAvailabilityDto,
    VehicleImageDto,
} from "@/shared/api-contract/api-contract";
import { GetCarDetailsResponse } from "@/shared/api-contract/GetCarDetails";
import { useI18n } from "@/shared/composables/use-i18n";
import { shallowRef, watch } from "vue";
import { Options, setup, Vue } from "vue-class-component";
import { useRouter } from "vue-router";
import { getEnvValue } from "@/shared/models/environment";
import AppVehicleOptions from "@/shared/components/app-vehicle-options/app-vehicle-options.vue";

import { Prop } from "vue-property-decorator";
import { formatDate, getEndDate } from "@/shared/utils";
import isNil from "lodash/isNil";
import {
    IWarantyBattery,
    IPricing,
    IWaranty,
} from "@/pages/auth/vehicle-details/vehicle-details";
import { VehicleFactoryEquipment } from "@/shared/vehicles/vehicle-factory-equipment/vehicle-factory-equipment";
import { eff } from "@/core/utils/effect-ts-abbr";
import { BRAND_LABELS } from "@/shared/constants";

@Options({
    components: {
        "app-vehicle-options": AppVehicleOptions,
        "app-vehicle-factory-equipment": VehicleFactoryEquipment,
    },
})
export class AppVehiclesPrint extends Vue {
    @Prop({ default: [] })
    initialCarIds!: number[];
    @Prop({ default: "" })
    id!: string;
    @Prop({ default: () => {} })
    printReadyCallback!: () => void;
    @Prop({ default: [] })
    initialCarDetails!: GetCarDetailsResponse[];
    @Prop({ default: {} })
    initialImages!: { [key: number]: VehicleImageDto[] };
    @Prop({ default: {} })
    initialOptions!: { [key: number]: OptionDto[] };
    @Prop({ default: [] })
    pricingMarket!: IPricing[];
    @Prop({ default: [] })
    pricingCatalog!: IPricing[];
    @Prop({ default: {} })
    warrantyBattery!: IWarantyBattery;
    @Prop({ default: {} })
    warranty!: IWaranty;
    @Prop({ default: {} })
    vehicleFactoryEquipments!: { [key: number]: string[] };
    @Prop({ default: {} })
    vehicleDocumentAvailability!: { [key: number]: VehicleDocumentAvailabilityDto[] };
    @Prop({ default: {} })
    vehicleAccessoryAvailability!: { [key: number]: VehicleAccessoryAvailabilityDto[] };
    @Prop({ default: {} })
    vehicleDamageRecord!: { [key: number]: VehicleDamageRecordDto[] };
    @Prop({ default: {} })
    vehicleDamage!: { [key: number]: VehicleDamageDto };
    storageApi = environment.storageApi;
    i18n = setup(useI18n);
    router = setup(useRouter);

    vehicleImageDtos = setup(() => shallowRef<{ [key: number]: VehicleImageDto[] }>({}));
    loadedImages = setup(() => shallowRef<{ [key: number]: [number] }>({}));
    carDetailsCollection = setup(() => shallowRef<readonly GetCarDetailsResponse[]>([]));
    allImagesLoaded = setup(() => shallowRef<boolean>(false));
    async mounted() {
        watch(
            () => ({
                images: this.allImagesLoaded,
            }),
            (newVal) => {
                if (newVal.images) {
                    this.printReadyCallback && this.printReadyCallback();
                }
            },
            { deep: true },
        );
    }

    updateLoadedImages(carId: number, imageId: VehicleImageDto["id"]) {
        const newLoadedImages = { ...this.loadedImages };
        newLoadedImages[carId] = newLoadedImages[carId] || [];
        newLoadedImages[carId].push(Number(imageId));
        this.loadedImages = newLoadedImages;
        this.allImagesLoaded = Object.keys(this.loadedImages).every(
            (carId) => this.loadedImages[carId]?.length === this.images[carId]?.length,
        );
    }

    getTargetDate(manufacturedDate) {
        if (manufacturedDate) {
            const epochStart = new Date(0);
            return new Date(
                epochStart.getTime() + manufacturedDate * 24 * 60 * 60 * 1000,
            );
        }
    }

    getYearFromDate(manufacturedDate) {
        const targetDate = this.getTargetDate(manufacturedDate);
        if (manufacturedDate && targetDate) {
            return targetDate.getFullYear();
        }
    }

    getMonthFromDate(manufacturedDate) {
        const targetDate = this.getTargetDate(manufacturedDate);
        if (manufacturedDate && targetDate) {
            const monthNames = [
                this.i18n.t("month.jan"),
                this.i18n.t("month.feb"),
                this.i18n.t("month.mar"),
                this.i18n.t("month.apr"),
                this.i18n.t("month.nay"),
                this.i18n.t("month.jun"),
                this.i18n.t("month.jul"),
                this.i18n.t("month.aug"),
                this.i18n.t("month.sep"),
                this.i18n.t("month.oct"),
                this.i18n.t("month.nov"),
                this.i18n.t("month.dec"),
            ];
            return monthNames[targetDate.getMonth()];
        }
    }

    getDayFromDate(manufacturedDate) {
        const targetDate = this.getTargetDate(manufacturedDate);
        if (manufacturedDate && targetDate) {
            return targetDate.getDate();
        }
    }

    get brandLabel() {
        return getEnvValue("BRAND_LABEL");
    }

    get images() {
        return this.initialImages || this.vehicleImageDtos;
    }

    carDetailsColumn(vehicleDto: GetCarDetailsResponse, index: number) {
        const result = this.carDetails(vehicleDto);
        return result.length > 0 ? result[index] ?? [] : [];
    }

    carDetails(vehicleDto: GetCarDetailsResponse) {
        const {
            id,
            bodytype,
            bodywork,
            brand,
            color,
            color_factory,
            color_interior,
            color_interior_factory,
            condition,
            finish,
            firstregistration_date,
            manufacturing_date,
            mileage,
            model,
            number_doors,
            number_seats,
            powertrain,
            upholstery,
            weight,
        } = vehicleDto;

        const result = [
            [
                {
                    title: this.i18n.t("carDetails.general"),
                    items: [
                        { name: this.i18n.t("carDetails.carID"), value: id },
                        {
                            name: this.i18n.t("carDetails.carCondition"),
                            value: condition?.name,
                        },
                        { name: this.i18n.t("carDetails.carBrand"), value: brand?.name },
                        { name: this.i18n.t("carDetails.carModel"), value: model },
                        {
                            name: this.i18n.t("carDetails.carMotorisation"),
                            value: powertrain?.motorisation,
                        },
                        { name: this.i18n.t("carDetails.carFinish"), value: finish },
                        {
                            name: this.i18n.t("carDetails.carDrivetrain"),
                            value: powertrain?.drivetrain?.name,
                        },
                        { name: this.i18n.t("carDetails.carMileage"), value: mileage },
                    ],
                },
                {
                    title: this.i18n.t("carDetails.engine"),
                    items: [
                        {
                            name: this.i18n.t("carDetails.carFueltype"),
                            value: powertrain?.fueltype?.name,
                        },
                        {
                            name: this.i18n.t("carDetails.carGearbox"),
                            value: powertrain?.gearbox?.name,
                        },
                        {
                            name: this.i18n.t("carDetails.carGears"),
                            value: powertrain?.specification.amount_of_gears,
                        },
                        {
                            name: this.i18n.t("carDetails.carPowerHP"),
                            value: powertrain?.power_hp,
                        },
                        {
                            name: this.i18n.t("carDetails.carPowerkW"),
                            value: powertrain?.power_kw,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine0"),
                            value: powertrain?.specification?.cylinder_amount,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine1"),
                            value: powertrain?.specification?.cylinder_capacity_cc,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine2"),
                            value: powertrain?.emission_standard?.name,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine3"),
                            value: powertrain?.specification?.emission_co2_nedc_combined,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine4"),
                            value: powertrain?.specification?.emission_co2_wltp_combined,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine5"),
                            value: powertrain?.specification?.wltp_consumption_low_l100km,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine6"),
                            value: powertrain?.specification
                                ?.wltp_consumption_medium_l100km,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine7"),
                            value: powertrain?.specification
                                ?.wltp_consumption_high_l100km,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine8"),
                            value: powertrain?.specification
                                ?.wltp_consumption_extrahigh_l100km,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine9"),
                            value: powertrain?.specification
                                ?.wltp_consumption_combined_l100km,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine10"),
                            value: powertrain?.specification
                                ?.nedc_consumption_urban_l100km,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine11"),
                            value: powertrain?.specification
                                ?.nedc_consumption_extraurban_l100km,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine12"),
                            value: powertrain?.specification
                                ?.nedc_consumption_combined_l100km,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine13"),
                            value: powertrain?.specification?.electric_autonomy_km,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine14"),
                            value: powertrain?.specification?.electric_autonomy_city_km,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine15"),
                            value: powertrain?.specification?.onboard_charger_type?.name,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine16"),
                            value: powertrain?.specification?.battery_capacity_kw,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine17"),
                            value: powertrain?.specification?.fast_charging_capable,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine18"),
                            value: powertrain?.specification?.charging_capacity_kw,
                        },
                        {
                            name: this.i18n.t("carDetails.carEngine19"),
                            value: powertrain?.specification?.fast_charging_capacity_kw,
                        },
                    ],
                },
            ],
            [
                {
                    title: this.i18n.t("carDetails.body"),
                    items: [
                        {
                            name: this.i18n.t("carDetails.carBody1"),
                            value: bodywork?.name,
                        },
                        {
                            name: this.i18n.t("carDetails.carBody2"),
                            value: bodytype?.name,
                        },
                        { name: this.i18n.t("carDetails.carBody3"), value: number_doors },
                        { name: this.i18n.t("carDetails.carBody4"), value: number_seats },
                        { name: this.i18n.t("carDetails.carBody5"), value: color?.name },
                        {
                            name: this.i18n.t("carDetails.carBody6"),
                            value: color_factory,
                        },
                        {
                            name: this.i18n.t("carDetails.carBody7"),
                            value: color_interior?.name,
                        },
                        {
                            name: this.i18n.t("carDetails.carBody8"),
                            value: color_interior_factory,
                        },
                        {
                            name: this.i18n.t("carDetails.carBody9"),
                            value: upholstery?.name,
                        },
                        { name: this.i18n.t("carDetails.carBody10"), value: weight },
                    ],
                },
                {
                    title: this.i18n.t("carDetails.registration"),
                    items: [
                        {
                            name: this.i18n.t("carDetails.carReg1"),
                            value: formatDate(firstregistration_date, null),
                        },
                        { name: this.i18n.t("carDetails.carReg2"), value: null },
                        {
                            name: this.i18n.t("carDetails.carReg3"),
                            value: formatDate(manufacturing_date, null),
                        },
                        {
                            name: this.i18n.t("carDetails.carReg4"),
                            value: formatDate(this.warranty?.start_date, null),
                        },
                        {
                            name: this.i18n.t("carDetails.carReg5"),
                            value: this.warranty?.length_months,
                        },
                        {
                            name: this.i18n.t("carDetails.carReg6"),
                            value: getEndDate(
                                this.warranty?.start_date,
                                this.warranty?.length_months,
                            ),
                        },
                        {
                            name: this.i18n.t("carDetails.carReg7"),
                            value: this.warranty?.mileage_limit,
                        },
                        {
                            name: this.i18n.t("carDetails.carReg8"),
                            value: formatDate(this.warrantyBattery?.start_date, null),
                        },
                        {
                            name: this.i18n.t("carDetails.carReg9"),
                            value: this.warrantyBattery?.length_months,
                        },
                        {
                            name: this.i18n.t("carDetails.carReg10"),
                            value: getEndDate(
                                this.warrantyBattery?.start_date,
                                this.warrantyBattery?.length_months,
                            ),
                        },
                        {
                            name: this.i18n.t("carDetails.carReg11"),
                            value: this.warrantyBattery?.start_mileage,
                        },
                        {
                            name: this.i18n.t("carDetails.carReg12"),
                            value: this.warrantyBattery?.mileage_limit,
                        },
                        ...(vehicleDto?.vehicle_message ? [{
                            name: "vehicle_message",
                            value: vehicleDto?.vehicle_message,
                        }] : []),
                    ],
                },
                {
                    title: this.i18n.t("carDetails.pricing"),
                    items: [
                        ...this.pricingMarket.map((item) => ({
                            name: `${this.i18n.t("carDetails.carPricing1")} ${
                                item.country.iso3
                            }:`,
                            value: `${item.price} (${item.discount_percentage}%)`,
                        })),
                        ...this.pricingCatalog.map((item) => ({
                            name: `${this.i18n.t("carDetails.carPricing8")} ${
                                item.country.iso3
                            }:`,
                            value: `${item.price} (${item.discount_percentage}%)`,
                        })),
                    ],
                    info: this.i18n.t("carDetails.carPricingInfo"),
                },
            ],
        ];
        const result2 = result.map((col) => {
            return col
                .map(({ items, ...group }) => {
                    return {
                        ...group,
                        items: items.filter((i) => !isNil(i.value)),
                    };
                })
                .filter((i) => i.items.length > 0);
        });
        return result2;
    }

    isCarconnexPlusLabel(vehicleDto: GetCarDetailsResponse) {
        return vehicleDto?.provider !== null && vehicleDto?.provider.id !== 1;
    }

    isDamageLabel(id: number, vehicleId: number) {
        return this.vehicleDamageRecord[vehicleId]?.some((e) =>
            e.types.some((type) => type.id === id),
        );
    }

    get brandLabelText() {
        return BRAND_LABELS[this.brandLabel];
    }
}
